<template>
  <div class="flex min-h-0 flex-col">
    <div class="flex items-start justify-between gap-4 p-5 pb-3">
      <div class="min-w-0">
        <h3 class="m-0 text-base font-semibold text-gray-900">Line items — {{ po.poNumber }}</h3>
        <p class="mb-0 mt-1 text-xs text-gray-500">{{ view.rows.length }} line items · where the calculated quantities come from.</p>
      </div>
      <input v-model="filter" type="text" class="input w-56 shrink-0" placeholder="Filter products…" aria-label="Filter products" />
    </div>

    <div class="grid gap-2 border-y border-gray-200 bg-gray-50 px-5 py-2 text-[10px] font-bold tracking-wider text-gray-500" :style="gridStyle">
      <span>PRODUCT</span>
      <span class="text-right">QTY</span>
      <span v-for="typeCode in view.typeCodes" :key="typeCode" class="text-right">{{ typeCode }}</span>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto">
      <div
        v-for="(row, index) in filteredRows"
        :key="index"
        class="grid gap-2 border-b border-gray-100 px-5 py-2 text-sm"
        :class="row.counted ? 'text-gray-900' : 'bg-gray-50 text-gray-400'"
        :style="gridStyle"
      >
        <span class="flex min-w-0 flex-col">
          <span class="inline-flex items-center gap-2 font-semibold">
            <span class="truncate">{{ row.partNumber ?? '—' }}</span>
            <span v-if="row.unknown" class="badge shrink-0 bg-amber-100 text-amber-800">count unknown</span>
            <span v-else-if="!row.counted" class="badge shrink-0 bg-gray-100 text-gray-600">not counted</span>
          </span>
          <span class="line-clamp-2 text-[11px] text-gray-400">{{ row.description }}</span>
        </span>
        <span class="text-right tabular-nums">{{ row.quantity.toLocaleString() }}</span>
        <span v-for="typeCode in view.typeCodes" :key="typeCode" class="text-right tabular-nums">
          {{ row.totalsByType[typeCode] !== undefined ? row.totalsByType[typeCode].toLocaleString() : '—' }}
        </span>
      </div>
      <p v-if="filteredRows.length === 0" class="m-0 px-5 py-8 text-center text-sm text-gray-400">No product matches “{{ filter }}”.</p>
    </div>

    <div class="grid gap-2 border-t border-blue-200 bg-blue-50 px-5 py-2.5 text-sm" :style="gridStyle">
      <span class="font-bold text-gray-900">PO total</span>
      <span></span>
      <span v-for="typeCode in view.typeCodes" :key="typeCode" class="text-right font-bold tabular-nums text-blue-700">
        {{ (view.totals[typeCode] ?? 0).toLocaleString() }}
      </span>
    </div>

    <p class="m-0 border-t border-gray-100 px-5 py-3 text-[11px] leading-relaxed text-gray-500">
      Termination counts are parsed from part numbers — a misparsed product shows “count unknown”; correct it by editing the plan quantities in the schedule form. Splits across
      weeks are constrained by these line-item quantities.
    </p>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import type { PurchaseOrder } from '@/types/purchaseOrders';
  import { buildLineItemsView } from '@/utils/poLineItems';
  import type { LineItemsView } from '@/utils/poLineItems';

  // The table alone, with no surface of its own: it is rendered both as a dialog of its own
  // (from a block's actions menu) and as the right pane of the schedule dialog, and those two
  // own their framing.
  const props = defineProps<{ po: PurchaseOrder }>();

  const filter = ref('');
  watch(
    () => props.po.poNumber,
    () => {
      filter.value = '';
    }
  );

  const view = computed<LineItemsView>(() => buildLineItemsView(props.po));

  const filteredRows = computed(() => {
    const needle = filter.value.trim().toLowerCase();
    if (!needle) return view.value.rows;
    return view.value.rows.filter((row) => (row.partNumber ?? '').toLowerCase().includes(needle) || row.description.toLowerCase().includes(needle));
  });

  const gridStyle = computed(() => ({ gridTemplateColumns: `3fr 0.8fr repeat(${view.value.typeCodes.length}, 0.7fr)` }));
</script>
